import auth from "~/config/auth";
import { login } from "~/utils/Auth";
import Input from "~/components/Inputs/Input";
import Checkbox from "~/components/Inputs/Checkbox";
import { useRouter } from "next/router";
import { ReactSVG } from "react-svg";
import Link from "next/link";
import useUser from "~/utils/useUser";
import React, { useState } from "react";
import Head from "~/components/Meta/Head";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Layout from "~/components/layouts/LandingPage";
import { faSpinnerThird } from "@fortawesome/pro-duotone-svg-icons";

const SignIn = () => {
  const router = useRouter();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const { mutateUser } = useUser({
    redirectTo: router?.query?.redirectTo ? router.query.redirectTo : "/",
    redirectIfFound: false,
  });

  const style = {
    "--fa-primary-color": "rgba(234, 88, 12, 1)",
    "--fa-primary-opacity": 1,
  };
  const handleSubmit = async (event) => {
    event.preventDefault();
    const email = event.currentTarget.email.value;
    const password = event.currentTarget.password.value;
    const rememberMe = event.currentTarget.elements["remember-me"].checked;
    setError();
    setLoading(true);
    try {
      mutateUser(await login(email, password, rememberMe));
    } catch (error) {
      if (error.response.status == 401) {
        setError(
          <>
            <strong>Error</strong> The email address or password you entered is
            incorrect.
          </>
        );
      } else {
        setError(
          <>
            <strong>Error</strong> Something went wrong. Please try again later.
          </>
        );
      }
    }
    setLoading(false);
  };

  return (
    <>
      <Head
        title={"Sign into your account"}
        url={`${process.env.CLIENT_ORIGIN}/sign-in`}
        description={"Sign in to your RealEstateAgents.com account."}
      />
      <section className="signin">
        <div className="signin__container">
          <div className="signin__sidebar"></div>
          <div className="signin__content">
            <div className="signin-box">
              <form onSubmit={handleSubmit}>
                <div className="signin-box__logo">
                  <Link prefetch={false} legacyBehavior href={"/"}>
                    <a>
                      <picture>
                        <img src="/static/rea-logo-with-icon-black.png" />
                      </picture>
                    </a>
                  </Link>
                </div>
                <h1 className="signin-box__heading">Welcome back!</h1>
                <div className="signin-box__text">
                  To login enter your email address and password.
                </div>
                <div className="signin-box__grid">
                  {error && (
                    <div className="alert --error --full-width --fadeDownIn">
                      {error}
                    </div>
                  )}
                  <Input
                    className="--full-width"
                    label="Email"
                    type="email"
                    name="email"
                    autoComplete={"email"}
                    required
                  />
                  <Input
                    className="--full-width"
                    label="Password"
                    type="password"
                    name="password"
                    autoComplete={"current-password"}
                    required
                  />
                  <Checkbox
                    label="Remember me"
                    name="remember-me"
                    checked={true}
                  />
                  <Link prefetch={false} legacyBehavior href="/forgot-password">
                    <a className="--text-right">Forgot your password?</a>
                  </Link>
                </div>
                <button className="signin-box__button" disabled={loading}>
                  {loading && (
                    <>
                      <FontAwesomeIcon
                        icon={faSpinnerThird}
                        spin
                        style={style}
                      />
                      &nbsp;
                    </>
                  )}
                  Sign In
                </button>
                {auth.allow3rdParty && (
                  <>
                    <div className="signin-box__divider">
                      <div className="signin-box__divider-text">
                        Or connect with
                      </div>
                    </div>
                    <ul className="signin-box__3rd-party">
                      <li>
                        <button
                          className="signin-box__3rd-party-button"
                          title="Sign in with Apple"
                        >
                          <ReactSVG src="/static/apple-icon.svg" />
                        </button>
                      </li>
                      <li>
                        <button
                          className="signin-box__3rd-party-button"
                          title="Sign in with Google"
                        >
                          <ReactSVG src="/static/google-icon.svg" />
                        </button>
                      </li>
                      <li>
                        <button
                          className="signin-box__3rd-party-button"
                          title="Sign in with Facebook"
                        >
                          <ReactSVG src="/static/facebook-icon.svg" />
                        </button>
                      </li>
                    </ul>
                  </>
                )}
                {auth.allowUserSignup && auth.allowAgentSignup && (
                  <>
                    <div className="signin-box__footer">
                      Don’t have an account?
                      <Link prefetch={false} legacyBehavior href="/sign-up">
                        <a> Sign up</a>
                      </Link>
                    </div>
                  </>
                )}
                {!auth.allowUserSignup && auth.allowAgentSignup && (
                  <>
                    <div className="signin-box__footer">
                      Don’t have an account? <br />
                      If you are a Real Estate Agent,{" "}
                      <Link
                        prefetch={false}
                        legacyBehavior
                        href="/signup"
                      >
                        <a>create your account here</a>
                      </Link>
                      .
                    </div>
                  </>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export async function getStaticProps(ctx) {
  if (!auth.allowLogin) {
    return {
      props: { statusCode: 403 },
    };
  }
  return {
    props: {},
  };
}

SignIn.getLayout = function getLayout(page) {
  return <Layout>{page}</Layout>;
};

export default SignIn;
